import React from "react";
import { useFormContext } from "react-hook-form";

import {
	HookFormInputGroup,
	HookFormSwitch,
	HookSelectFormGroup,
} from "components";
import { DebounceTimeout } from "globalConstants";
import { EnumItem, SubscriptionOptionsType } from "types";
import {
	floatWithComma2Normalizer,
	minValueEx1,
	commaAndFloat2Normalizer,
	onlyMaxDigits2,
	required,
} from "validators";

import styles from "./posSubscriptionSection.module.scss";

type CostInputProps = {
	service: { name: string; displayName: string };
	cadenceDdl: EnumItem[];
	isLoading?: boolean;
	status: boolean;
	isQuantityDisabled?: boolean;
	name: string;
	handleCadenceChange?: (value: string, name: string) => void;
	handleQuantityChange: (name: string) => void;
	handleStatusChange: (value: boolean) => void;
};

export const useGetRow = ({
	service,
	cadenceDdl,
	isLoading = false,
	isQuantityDisabled = false,
	handleCadenceChange,
	handleQuantityChange,
	handleStatusChange,
	status,
	name,
}: CostInputProps) => {
	const {
		register,
		setValue,
		formState: { errors },
		watch,
	} = useFormContext<{
		[key: string]: {
			subscriptions: { discount: string } & SubscriptionOptionsType;
		};
	}>();

	const getSubscriptionTitle = (value: boolean) => {
		return (
			<div className={styles.subscriptionTitle}>
				{value ? "Subscribed" : "Unsubscribed"}
			</div>
		);
	};

	const quantity = watch(`${name}.quantity`);

	const isDisabled = !status;

	const disabledClass = isDisabled ? styles.disabled : "";

	return [
		{ className: styles.emptyColumn },
		{
			value: <div className={styles.label}>{service.displayName}</div>,
		},
		{
			value: (
				<HookSelectFormGroup
					options={cadenceDdl}
					getOptionLabel={option => option.displayName}
					getOptionValue={option => option.name}
					placeholder="Please Select"
					name={`${name}.billingCadence`}
					isSearchable={false}
					cacheOptions
					defaultOptions
					debounceTimeout={DebounceTimeout}
					blurLoading={isLoading}
					hideLabel
					colSelect={{ sm: 12 }}
					containerClassName={`${styles.cadenceSelect} ${disabledClass} form-group-mb-0`}
					isDisabled={isDisabled}
					onChange={value =>
						handleCadenceChange?.(value, service.name)
					}
				/>
			),
		},
		{
			value:
				quantity === null ? (
					<div
						className={`${styles.na} ${
							isDisabled ? styles.disabled : ""
						}`}
					>
						N/A
						<HookFormInputGroup
							name={`${name}.quantity`}
							register={register}
							readOnly
							type="hidden"
						/>
					</div>
				) : (
					<HookFormInputGroup
						name={`${name}.quantity`}
						register={register}
						colInput={12}
						blurLoading={isLoading}
						autoComplete="off"
						placeholder=""
						readOnly={isDisabled || isQuantityDisabled}
						disabled={isDisabled || isQuantityDisabled}
						validator={status ? { minValueEx1, required } : {}}
						normalize={onlyMaxDigits2}
						errors={errors}
						inputBlockClassName={styles.quantityInput}
						className="form-group-mb-0"
						onChange={() => handleQuantityChange(service.name)}
					/>
				),
		},
		{
			value: (
				<HookFormInputGroup
					name={`${name}.cost`}
					register={register}
					colInput={12}
					blurLoading={isLoading}
					autoComplete="off"
					placeholder=""
					readOnly={isDisabled}
					disabled={isDisabled}
					normalizeOnBlur={floatWithComma2Normalizer}
					normalize={commaAndFloat2Normalizer}
					solveCaretIssue={commaAndFloat2Normalizer}
					validator={status ? { required } : {}}
					errors={errors}
					inputBlockClassName={styles.costInput}
					className="form-group-mb-0"
				/>
			),
		},
		{
			value: getSubscriptionTitle(status),
		},
		{
			value: (
				<HookFormSwitch
					name={`${name}.isSubscribed`}
					register={register}
					setValue={setValue}
					initialValue={status}
					className={styles.switchWrapper}
					onChange={handleStatusChange}
				/>
			),
		},
		{ className: styles.emptyColumn },
	];
};
